import {
  getActiveUser,
  navigate,
  sendGetRequest,
  sendPostRequest,
  sendDeleteRequest,
  getParams,
  formatDate,
  showSnack,
  allowedOperations,
} from "../utils/utils";
import DataListWithSearch from "./component/@list/list-search";
import { useState, useEffect } from "react";
import AppRoutes from "../routing/routes";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import EasyTextEdit from "./component/text-edit";
import { Dropzone, FileMosaic } from "@files-ui/react";

const getTableHead = () => {
  return [
    { id: "createdAt", label: "Add On", alignRight: false },
    { id: "language", label: "Language", alignRight: false },
    { id: "isoCode", label: "Iso Code", align: "center" },
    { id: "size", label: "Size", alignRight: false },
    { id: "link", label: "Link", alignRight: false },
  ];
};

const subRef = {
  isoCode: { value: "", touched: false },
  url: { value: "", touched: false },
};

const ContentSubtitleList = () => {
  const { contentId } = getParams();
  const loggedUser = getActiveUser();
  const [content, setContent] = useState({});
  const [files, setFiles] = useState([]);
  const [subtitles, setSubtitles] = useState([]);
  const [subtitle, setSubtitle] = useState(subRef);
  const [title, setTitle] = useState(null);
  const [open, openDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [loading, onLoading] = useState(false);
  const [srtSource, setSrtSource] = useState("file");
  const { canEdit, canDelete, canCreate } = allowedOperations();

  const userOptions = [
    canDelete ? {
      icon: "delete",
      value: "delete",
      label: "Delete",
      onClick: (value, selected) => {
        const _sub = subtitles.find((sub) => sub.isoCode === selected.join());
        setSubtitle({
          isoCode: { value: _sub.isoCode, touched: true },
          url: { touched: true, value: _sub.url },
        });
        setDeleteDialog(true);
      },
    }: null,
    canEdit ? {
      icon: "edit",
      value: "edit",
      label: "Edit",
      onClick: (_value, selected) => {
        const _sub = subtitles.find((sub) => sub.isoCode === selected.join());
        setSubtitle({
          isoCode: { value: _sub.isoCode, touched: true },
          url: { touched: true, value: _sub.url },
        });
        openDialog(true);
      },
    }: null,
  ].filter(Boolean);
  const filterOptions = [];

  const isS3Source = () => srtSource === "s3";

  const isValidSrtFile = () => {
    if (isS3Source()) {
      return (
        subtitle.url.value != "" &&
        subtitle.url.touched &&
        subtitle.url.value.endsWith("srt")
      );
    } else {
      return files.length === 1 && files[0].name.endsWith(".srt");
    }
  };

  const getSubProps = () => {
    const formData = new FormData();
    for (const [key, value] of Object.entries({ ...subtitle })) {
      formData.append(key, value.value);
    }

    if (files.length === 1) {
      files.forEach((file) => {
        formData.append("files", file.file);
      });
    }
    return formData;
  };

  const isUrlError = () =>
    (isS3Source() ? subtitle.url.touched : false) && !isValidSrtFile();

  const isValidForm = () =>
    subtitle.isoCode.value != "" &&
    subtitle.isoCode.touched &&
    isValidSrtFile();

  const handleDeleteSubs = () => {
    onLoading(true);
    sendDeleteRequest(
      `contents/${subtitle.isoCode.value}/subtitles`,
      contentId
    ).then((_) => {
      closeDeleteDialog();
      executeFetch();
      showSnack("Subtitle deleted successfully");
    });
  };

  const handleProcessSubTitle = () => {
    onLoading(true);
    sendPostRequest(`contents/${contentId}/subtitles`, getSubProps()).then(
      () => {
        closeDialog();
        executeFetch();
        showSnack("Subtitle has beed added successfully");
      }
    );
  };

  const closeDialog = () => {
    setSubtitle(subRef);
    openDialog(false);
    setFiles([]);
  };

  const closeDeleteDialog = () => {
    setSubtitle(subRef);
    setDeleteDialog(false);
  };

  const executeFetch = () => {
    if (!contentId) {
      navigate(`${AppRoutes.contents}`, true);
      return;
    }
    Promise.all([
      sendGetRequest(`languages`),
      sendGetRequest(`contents/${contentId}/subtitles`, (loading) =>
        onLoading(loading)
      ),
      sendGetRequest(`contents?uuid=${contentId}`),
    ]).then(([languages, { title, subtitles: _subtitles }, content]) => {
      setLanguages(languages);
      setSubtitles(_subtitles);
      setTitle(title);
      setSubtitle({
        ...subRef,
        ...{ isoCode: { touched: true, value: content.language } },
      });
    });
  };

  useEffect(
    () => executeFetch(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentId]
  );

  const updateFiles = (selectedFiles) => {
    setFiles(selectedFiles);
  };

  const onDelete = (id) => {
    setFiles(files.filter((_file) => _file.id !== id));
  };

  return (
    <div>
      <DataListWithSearch
        sectionTitle={title ? `${title} subtitles` : "..."}
        createNewLabel={loggedUser.active && canCreate ? "Add Subtitle" : null}
        data={subtitles}
        primaryKey="isoCode"
        filterColumn={"createdAt"}
        emptyIcon="subtitles"
        avatarKey="isoCode"
        loading={loading}
        onCreateNewItem={() => openDialog(true)}
        headers={getTableHead()}
        onBack={() => navigate(`${AppRoutes.contents}`, true)}
        rowData={(data) => {
          const _entries = [
            {
              value: formatDate(data.createdAt, "hh:mm A  MMM DD, YYYY"),
              width: 100,
            },
            { value: data.language, width: 120 },
            { value: data.isoCode, width: 90, align: "center" },
            { value: data.size.label, width: 90 },
            { value: data.url, width: 250 },
          ];
          return {
            exclude: [],
            entries: _entries,
          };
        }}
        selectionOptions={userOptions.filter(
          (option) => option.value !== "edit"
        )}
        filterOptions={filterOptions}
        optionItems={userOptions}
      />

      <Dialog
        open={deleteDialog}
        onClose={() => closeDeleteDialog()}
        fullWidth
        maxWidth={"sm"}
      >
        <DialogTitle> Delete confirmation</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to delete a{" "}
            <strong>
              {languages.length && subtitle.isoCode.value != ""
                ? languages.find(
                    (lan) => lan.isoCode === subtitle.isoCode.value
                  ).name || ""
                : ""}
            </strong>{" "}
            subtitle from <strong>{title}</strong> video, would you like to
            proceed?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ pb: 4 }}>
          <Button onClick={() => closeDeleteDialog()}>Cancel</Button>
          <Button
            onClick={() => handleDeleteSubs()}
            sx={{ mr: 3, pl: 2, pr: 2 }}
          >
            {loading && <CircularProgress color="primary" />}
            {loading ? "" : "Delete Now"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={() => closeDialog()}
        fullWidth
        maxWidth={"lg"}
      >
        <DialogTitle> New subtitle</DialogTitle>
        <DialogContent>
          <Grid container direction="row" sx={{ py: 4 }} spacing={4}>
            <Grid item xs={12} md={3}>
              <FormControl sx={{ minWidth: 50 }} fullWidth>
                <InputLabel id="lan-label">Language</InputLabel>
                <Select
                  value={subtitle.isoCode.value}
                  label="Language"
                  labelId="lan-label"
                  id="language"
                  onChange={(e) =>
                    setSubtitle({
                      ...subtitle,
                      isoCode: { value: e.target.value, touched: true },
                    })
                  }
                >
                  {languages.map((language) => {
                    return (
                      <MenuItem value={language.isoCode} id={language.uuid}>
                        &nbsp;&nbsp;&nbsp;{language.name}
                        <br />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl sx={{ minWidth: 50 }} fullWidth>
                <InputLabel id="type-label">Source</InputLabel>
                <Select
                  value={srtSource}
                  label="Source"
                  labelId="type-label"
                  id="source"
                  onChange={(e) => {
                    if (e.target.value != srtSource) {
                      setFiles([]);
                      setSubtitle({
                        ...subtitle,
                        ...{ url: { value: "", touched: false } },
                      });
                    }
                    setSrtSource(e.target.value);
                  }}
                >
                  {[
                    { key: "s3", value: "S3" },
                    { key: "file", value: "File" },
                  ].map((source) => {
                    return (
                      <MenuItem value={source.key} id={source.key}>
                        &nbsp;&nbsp;&nbsp;{source.value}
                        <br />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {srtSource === "s3" && (
              <Grid item xs={12} md={7}>
                <EasyTextEdit
                  placeholder="Subtitle URL"
                  type="text"
                  id="url"
                  error={isUrlError()}
                  errorText={
                    isUrlError() ? "Make sure it's a valid subtitle file" : ""
                  }
                  textField={true}
                  disabled={loading}
                  value={subtitle.url.value}
                  onChange={(value) =>
                    setSubtitle({ ...subtitle, url: { value, touched: true } })
                  }
                />
              </Grid>
            )}

            {srtSource === "file" && (
              <Grid item md={12} xs={12}>
                <Dropzone
                  onChange={updateFiles}
                  value={files}
                  minHeight="195px"
                  maxFiles={1}
                  accept={".srt"}
                  maxFileSize={1024 * 1024 * 5}
                  label="Drag'n drop SRT file or click to browse"
                >
                  {files.map((file) => (
                    <FileMosaic
                      {...file}
                      resultOnTooltip
                      alwaysActive
                      preview
                      info
                      onDelete={onDelete}
                      onSee={() => {}}
                      onWatch={() => {}}
                    />
                  ))}
                </Dropzone>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 4 }}>
          <Button onClick={() => closeDialog()}>Cancel</Button>
          <Button
            disabled={!isValidForm()}
            onClick={() => handleProcessSubTitle()}
            sx={{ mr: 3, pl: 2, pr: 2 }}
          >
            {loading && <CircularProgress color="primary" />}
            {loading ? "" : "Add Now"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContentSubtitleList;
